import { createRouter, createWebHistory } from "vue-router";
import Login from "@/views/Login.vue";
import SignUp from "@/views/SignUp.vue";
import ForgotPassword from "@/views/ForgotPassword.vue";
import ResetPassword from "@/views/ResetPassword.vue";
import VerifyEmail from "@/components/auth/VerifyEmail.vue";
import DashboardView from "@/views/DashboardView.vue";
import PricingView from "@/views/PricingView.vue";
import Profile from "@/views/Profile.vue";
import Settings from "@/views/Settings.vue";
import Projects from "@/views/Projects.vue";
import ProjectDetails from "@/views/ProjectDetails.vue";
import ProjectAssets from "@/views/ProjectAssets.vue";
import TeamsView from "@/views/TeamsView.vue";
import TalentIntelligenceView from "@/views/TalentIntelligenceView.vue";
import TalentProfileView from "@/views/TalentProfileView.vue";
import NotFound from "@/views/NotFound.vue";
import Unauthorized from "@/components/Unauthorized.vue";
import store from "@/store";

// Define role-checking function
const checkRole = (allowedRoles) => (to, from, next) => {
  const roleFromStorage = localStorage.getItem("role");
  const roleFromStore = store.state.role;
  const userRole = roleFromStore || roleFromStorage;

  const tokenFromStorage = localStorage.getItem("token");
  const tokenFromStore = store.state.token;
  const userFromStore = store.state.user;
  const isAuthenticated =
    (tokenFromStorage && tokenFromStore && userFromStore) ||
    (tokenFromStorage && userFromStore);

  if (!isAuthenticated) {
    next({ name: "Login" });
  } else if (allowedRoles.includes(userRole)) {
    next();
  } else {
    next({ name: "Unauthorized" });
  }
};

const routes = [
  { path: "/", redirect: "/dashboard" },
  { path: "/login", name: "Login", component: Login },
  { path: "/signup", name: "SignUp", component: SignUp },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
  },
  { path: "/reset-password", name: "ResetPassword", component: ResetPassword },
  { path: "/verify-email/:email", name: "VerifyEmail", component: VerifyEmail },
  
  {
    path: "/dashboard",
    name: "Dashboard",
    component: DashboardView,
    meta: { requiresAuth: true, label: "Dashboard" },
    beforeEnter: checkRole(["admin", "manager", "talent", "investor"]),
  },
  {
    path: "/pricing",
    name: "Pricing",
    component: PricingView,
    meta: { requiresAuth: true, label: "Pricing" },
    beforeEnter: checkRole(["admin", "manager", "talent", "investor"]),
  },
  
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true, label: "Profile" },
    beforeEnter: checkRole(["admin", "manager", "talent", "investor"]),
  },
  
  {
    path: "/settings",
    name: "Settings",
    component: Settings,
    meta: { requiresAuth: true, label: "Settings" },
    beforeEnter: checkRole(["admin", "manager", "talent", "investor"]),
  },
  
  {
    path: "/projects",
    name: "Projects",
    component: Projects,
    meta: { requiresAuth: true, label: "Projects" },
    beforeEnter: checkRole(["admin", "manager", "talent", "investor"]),
  },
  {
    path: "/projects/:projectId",
    name: "ProjectDetails",
    component: ProjectDetails,
    meta: { requiresAuth: true, label: "Project Details" },
    beforeEnter: checkRole(["admin", "manager", "talent", "investor"]),
  },
  {
    path: "/projects/:projectId/assets",
    name: "ProjectAssets",
    component: ProjectAssets,
    meta: { requiresAuth: true, label: "Project Assets" },
    beforeEnter: checkRole(["admin", "manager", "talent"]),
  },
  
  {
    path: "/teams",
    name: "Teams",
    component: TeamsView,
    meta: { requiresAuth: true, label: "Teams" },
    beforeEnter: checkRole(["admin", "manager", "talent"]),
  },
  
  {
    path: "/talent-pool",
    name: "TalentIntelligence",
    component: TalentIntelligenceView,
    meta: { requiresAuth: true, label: "Talent Pool" },
    beforeEnter: checkRole(["admin", "manager", "investor"]),
  },
  {
    path: "/talent-pool/:id",
    name: "TalentProfile",
    component: TalentProfileView,
    meta: { requiresAuth: true, label: "Talent Profile" },
    beforeEnter: checkRole(["admin", "manager", "investor"]),
  },

  { path: "/unauthorized", name: "Unauthorized", component: Unauthorized },
  { path: "/:pathMatch(.*)*", name: "NotFound", component: NotFound },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Global route guard for authentication
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("token");

  if (to.path === "/" && token) {
    next({ name: "Dashboard" });
  } else if (to.path === "/" && !token) {
    next({ name: "Login" });
  } else if ((to.name === "Login" || to.name === "SignUp") && token) {
    next({ name: "Projects" });
  } else if (to.name === "Login") {
    next();
  } else if (to.matched.some((record) => record.meta.requiresAuth) && !token) {
    next({ name: "Login" });
  } else {
    next();
  }
});

export default router;
